import type { SheetSyncSummary } from './infoserviceApi.js'

/** « à l’instant », « il y a 4 min », « il y a 2 h », « le 18/10 à 09:12 ». */
export function sheetSyncAgo(iso: string, now: Date = new Date()): string {
  const at = new Date(iso)
  const minutes = Math.floor((now.getTime() - at.getTime()) / 60_000)
  if (minutes < 1) return 'à l’instant'
  if (minutes < 60) return `il y a ${minutes} min`
  if (minutes < 12 * 60) return `il y a ${Math.floor(minutes / 60)} h`
  const day = at.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' })
  const time = at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
  return `le ${day} à ${time}`
}

export interface SheetSyncChangeLine {
  count: number
  label: string
  names: string[]
  more: boolean
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return count > 1 ? pluralForm : singular
}

/**
 * Ce qu'une synchronisation a changé, en lignes courtes. Une ligne à zéro
 * n'est pas affichée ; sans aucun changement, une seule ligne le dit.
 */
export function sheetSyncChanges(summary: SheetSyncSummary, options: { firstConnection: boolean }): SheetSyncChangeLine[] {
  const sample = (names: string[], count: number) => ({ names, more: count > names.length })
  const lines: SheetSyncChangeLine[] = []
  if (options.firstConnection) {
    if (summary.adopted) lines.push({ count: summary.adopted, label: `${plural(summary.adopted, 'annonce déjà dans TrafficPlay reprise', 'annonces déjà dans TrafficPlay reprises')}, avec ${plural(summary.adopted, 'son image', 'leurs images')}`, names: [], more: false })
    if (summary.created) lines.push({ count: summary.created, label: plural(summary.created, 'nouvelle annonce, sans image', 'nouvelles annonces, sans image'), ...sample(summary.createdNames, summary.created) })
    if (summary.archived) lines.push({ count: summary.archived, label: `${plural(summary.archived, 'annonce absente', 'annonces absentes')} de la feuille, ${plural(summary.archived, 'archivée', 'archivées')}`, ...sample(summary.archivedNames, summary.archived) })
    return lines
  }
  if (summary.created) lines.push({ count: summary.created, label: plural(summary.created, 'nouvelle annonce', 'nouvelles annonces'), ...sample(summary.createdNames, summary.created) })
  if (summary.updated) lines.push({ count: summary.updated, label: `${plural(summary.updated, 'annonce modifiée', 'annonces modifiées')} (jours, mail, repères)`, names: [], more: false })
  if (summary.renamed) {
    lines.push({
      count: summary.renamed,
      label: `${plural(summary.renamed, 'annonce renommée', 'annonces renommées')}, image gardée`,
      ...sample(summary.renamedNames.map(entry => `${entry.from} → ${entry.to}`), summary.renamed),
    })
  }
  if (summary.adopted) lines.push({ count: summary.adopted, label: plural(summary.adopted, 'annonce reprise', 'annonces reprises'), names: [], more: false })
  if (summary.archived) lines.push({ count: summary.archived, label: `${plural(summary.archived, 'ligne disparue', 'lignes disparues')} de la feuille, ${plural(summary.archived, 'archivée', 'archivées')}`, ...sample(summary.archivedNames, summary.archived) })
  if (lines.length === 0) lines.push({ count: summary.rows, label: `${plural(summary.rows, 'annonce', 'annonces')}, aucun changement`, names: [], more: false })
  return lines
}
